import { createSlice } from "@reduxjs/toolkit";
import { ROLES, STATUSES } from "../../util/constants";
import { handleLogout, syncAmplifyReduxUserAttributes } from "./thunks";
import { sliceNames } from "../util/constants";
import { parseOSUID } from "../../util/functions";

const initialState = {
  redirectPath: undefined,
  user: {},
  status: STATUSES.IDLE
};

const authenticationSlice = createSlice({
  name: sliceNames.authentication,
  initialState,
  reducers: {
    setUserAttributes(state, action) {
      state.user = action?.payload;
      state.status = STATUSES.SUCCESS
    },
    setUserRoles(state, action) {
      state.user.roles = action?.payload;
    },
    setRedirectPath(state, action) {
      state.redirectPath = action?.payload;
    },
    clearUserData(state, action) {
      state.user = {}
      state.status = STATUSES.CLEARED
    },
  },
  extraReducers: (builder) => {
      builder
        .addCase(syncAmplifyReduxUserAttributes.pending, (state) => {
          state.status = STATUSES.LOADING;
        })
        .addCase(syncAmplifyReduxUserAttributes.fulfilled, (state) => {
          state.status = STATUSES.SUCCESS;
        })
        .addCase(syncAmplifyReduxUserAttributes.rejected, (state) => {
          state.status = STATUSES.ERROR;
        })
        .addCase(handleLogout.pending, (state) => {
          state.status = STATUSES.CLEARING;
        })
        .addCase(handleLogout.fulfilled, (state) => {
          state.status = STATUSES.CLEARED;
        })
        .addCase(handleLogout.rejected, (state) => {
          state.status = STATUSES.ERROR;
          state.user = {};
        })
  },
});

const selectOauthRedirectPath = (state) => {
  return state?.authentication?.redirectPath;
};

const selectAuthenticatedUser = (state) => {
  const user = state?.authentication?.user;
  const osuid = parseOSUID(user || {})
  return {
    ...user,
    osuid
  };
};

const selectName = (state) => {
  const user = selectAuthenticatedUser(state)
  return user?.nickname ?? user?.name
};


const selectAuthenticatedUserRoles = (state) => {
  return selectAuthenticatedUser(state)?.roles || [];
};

const selectIsAdmin = (state) => {
  return selectAuthenticatedUserRoles(state)?.includes(ROLES.ADMINS)
};

const selectIsUser= (state) => {
  return selectAuthenticatedUserRoles(state)?.includes(ROLES.USERS)
};

const selectCanCreateForms = (state) => {
  return selectIsUser(state) || selectIsAdmin(state)
};

const selectAuthenticatedUserSessionID = (state) => {
  return selectAuthenticatedUser(state)?.sub;
};

const selectAuthStatus = (state) => {
  return state?.authentication?.status;
};

const selectIsLoggedIn = (state) => {
  const user = selectAuthenticatedUser(state);

  return selectAuthStatus(state) === STATUSES.SUCCESS && !!user?.osuid;
};

const selectLoggedInEmplid = (state) => {
  const user = selectAuthenticatedUser(state);
  return user?.osuid || user?.idmid
};

const selectIsLoggingIn = (state) => {
  return selectAuthStatus(state) === STATUSES.LOADING;
};

const selectIsLoggingOut = (state) => {
  return selectAuthStatus(state) === STATUSES.CLEARING;
};

const { setRedirectPath, setUserAttributes, setUserRoles, clearUserData } = authenticationSlice.actions;
const { reducer, name } = authenticationSlice;
export {
  selectOauthRedirectPath,
  setRedirectPath,
  setUserAttributes,
  selectAuthenticatedUser,
  selectAuthenticatedUserSessionID,
  selectAuthenticatedUserRoles,
  setUserRoles,
  name,
  clearUserData,
  selectIsLoggingOut,
  selectAuthStatus,
  selectIsLoggingIn,
  selectIsLoggedIn,
  selectLoggedInEmplid,
  selectIsAdmin,
  selectIsUser,
  selectName,
  selectCanCreateForms,
  reducer as default,
};
